import type { EventAttributes } from 'ics';

/**
 * When a reservation actually happens, from what the restaurant booked rather
 * than from `seatingDate`.
 *
 * `seatingDate` is the booked wall clock read as Europe/Stockholm: one hour late
 * for Finland, and 24 hours early for a seating after midnight. booking-api is
 * going to correct it (booking-api docs/booking-ids-time-cleanup-plan.md), so
 * nothing here may depend on it being either wrong or right. The truth is
 * `dateString` (the restaurant's business day, YYYYMMDD) and the quartiles
 * (quartile 0 is 06:00, 15 minutes each, and from quartile 72 on the clock is
 * past midnight, on the next calendar day), in the venue's own timezone.
 *
 * Same semantics as booking-api's `Time.instant()`, so the two always agree.
 */

/**
 * Every market is in one timezone, so the country decides it. Stored timezones
 * cannot be trusted: the catalog's venue `timezone` is Europe/Stockholm for
 * several NO, DK and DE venues. Same map as booking-api's TZ_BY_COUNTRY and the
 * pipeline's.
 */
export const TZ_BY_COUNTRY: Readonly<Record<string, string>> = Object.freeze({
  SE: 'Europe/Stockholm',
  NO: 'Europe/Oslo',
  DK: 'Europe/Copenhagen',
  FI: 'Europe/Helsinki',
  DE: 'Europe/Berlin',
  GB: 'Europe/London',
});

const FIRST_QUARTILE_MINUTES = 6 * 60;
const MINUTES_PER_QUARTILE = 15;
const MINUTE_MS = 60 * 1000;
const DAY_MS = 24 * 60 * MINUTE_MS;

export function timezoneForCountry(countryCode: string): string {
  const code = typeof countryCode === 'string' ? countryCode.toUpperCase() : '';
  if (!Object.prototype.hasOwnProperty.call(TZ_BY_COUNTRY, code)) {
    throw new Error(`No timezone for country ${JSON.stringify(countryCode)}`);
  }
  return TZ_BY_COUNTRY[code];
}

/**
 * The venue's country: its `countryCode` from the venue JSON when there is one,
 * otherwise the prefix of the venue slug ("fi-helsinki-kamppi" is FI).
 */
export function countryForVenue({
  countryCode,
  venueId,
}: {
  countryCode?: string | null;
  venueId?: string | null;
}): string {
  if (typeof countryCode === 'string' && countryCode.trim()) {
    return countryCode.trim().toUpperCase();
  }
  const prefix = /^([a-z]{2})-/i.exec(String(venueId || ''));
  if (prefix) {
    return prefix[1].toUpperCase();
  }
  throw new Error(`No country for venue ${JSON.stringify(venueId)}`);
}

const formatters: Record<string, Intl.DateTimeFormat> = {};

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  if (!formatters[timeZone]) {
    formatters[timeZone] = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    });
  }
  return formatters[timeZone];
}

/** How many minutes the zone's clock is ahead of UTC at an instant. */
function zoneOffsetMinutes(utcMs: number, timeZone: string): number {
  const parts: Record<string, number> = {};
  for (const part of formatterFor(timeZone).formatToParts(new Date(utcMs))) {
    parts[part.type] = Number(part.value);
  }
  const wallClockAsUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour % 24, // some engines say 24 for midnight
    parts.minute,
    parts.second,
  );
  return Math.round((wallClockAsUtc - (utcMs - (utcMs % 1000))) / MINUTE_MS);
}

/**
 * The instant a wall clock reads in a zone. `wallClockMs` is that wall clock
 * written as if it were UTC. A wall clock that does not exist (the hour skipped
 * in spring) moves forward by the gap; one that exists twice (the hour repeated
 * in autumn) is the first of the two.
 */
function wallClockToInstant(wallClockMs: number, timeZone: string): Date {
  // The offsets on either side of the wall clock. They differ only on a night
  // the clocks change, and a market changes them at most twice a year.
  const offsetBefore = zoneOffsetMinutes(wallClockMs - DAY_MS, timeZone);
  const offsetAfter = zoneOffsetMinutes(wallClockMs + DAY_MS, timeZone);

  const valid = [offsetBefore, offsetAfter]
    .map((offset) => ({ offset, instant: wallClockMs - offset * MINUTE_MS }))
    .filter(({ offset, instant }) => zoneOffsetMinutes(instant, timeZone) === offset)
    .map(({ instant }) => instant);

  if (valid.length === 0) {
    // In the gap: read with the offset from before the change, which lands
    // the same distance past the gap as the wall clock was into it.
    return new Date(wallClockMs - offsetBefore * MINUTE_MS);
  }
  return new Date(Math.min(...valid));
}

function parseDateString(dateString: string): { year: number; month: number; day: number } {
  const match = /^(\d{4})(\d{2})(\d{2})$/.exec(typeof dateString === 'string' ? dateString : '');
  if (match) {
    const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
    const check = new Date(Date.UTC(year, month - 1, day));
    if (
      check.getUTCFullYear() === year &&
      check.getUTCMonth() === month - 1 &&
      check.getUTCDate() === day
    ) {
      return { year, month, day };
    }
  }
  throw new Error(`Invalid dateString ${JSON.stringify(dateString)}`);
}

/**
 * The instant a quartile starts on a business day, in the venue's country's
 * timezone. Quartile 72 is 00:00 and everything from there on is on the next
 * calendar day. Does not depend on the browser's (or server's) timezone.
 */
export function quartileInstant(dateString: string, quartile: number, countryCode: string): Date {
  const timeZone = timezoneForCountry(countryCode);
  const { year, month, day } = parseDateString(dateString);
  if (!Number.isInteger(quartile) || quartile < 0) {
    throw new Error(`Invalid quartile ${JSON.stringify(quartile)}`);
  }
  // Date.UTC rolls minutes past midnight over into the next day, month and year.
  const wallClockMs = Date.UTC(
    year,
    month - 1,
    day,
    0,
    FIRST_QUARTILE_MINUTES + quartile * MINUTES_PER_QUARTILE,
  );
  return wallClockToInstant(wallClockMs, timeZone);
}

export interface SeatingFields {
  dateString?: string;
  startQuartile?: number;
  endQuartile?: number;
  venueId?: string;
}

/**
 * When a reservation starts and ends. `countryCode` is the venue JSON's; without
 * it the venue slug's prefix decides. Throws when the reservation lacks a
 * business day or quartiles, or the venue's country is unknown.
 */
export function seatingInstants(
  reservation: SeatingFields,
  countryCode?: string | null,
): { start: Date; end: Date } {
  const country = countryForVenue({ countryCode, venueId: reservation?.venueId });
  return {
    start: quartileInstant(reservation?.dateString, reservation?.startQuartile, country),
    end: quartileInstant(reservation?.dateString, reservation?.endQuartile, country),
  };
}

/** Like `seatingInstants(...).start`, but null instead of throwing. */
export function seatingStartOrNull(
  reservation: SeatingFields,
  countryCode?: string | null,
): Date | null {
  try {
    const country = countryForVenue({ countryCode, venueId: reservation?.venueId });
    return quartileInstant(reservation?.dateString, reservation?.startQuartile, country);
  } catch {
    return null;
  }
}

/**
 * The business day as YYYY-MM-DD, for showing the day a reservation is for and
 * for sending it back to booking-api. Undefined when there is none, so callers
 * behave as they did with a missing date.
 */
export function businessDate(dateString?: string): string | undefined {
  try {
    const { year, month, day } = parseDateString(dateString);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${year}-${pad(month)}-${pad(day)}`;
  } catch {
    return undefined;
  }
}

/**
 * The "Add to calendar" event. Start and end are UTC instants, so the file is
 * the same whatever timezone the guest's browser is in, and a calendar shows
 * the seating at the restaurant's own time.
 */
export function reservationCalendarEvent(
  reservation: SeatingFields & { seatings?: number },
  venue: { countryCode?: string; title?: string; contact?: { phone?: string } } | undefined,
  reservationUrl: string,
): EventAttributes {
  const { start, end } = seatingInstants(reservation, venue?.countryCode);
  return {
    start: start.getTime(),
    startInputType: 'utc',
    startOutputType: 'utc',
    end: end.getTime(),
    endInputType: 'utc',
    endOutputType: 'utc',
    title: `Booking for ${reservation?.seatings} people at ${venue?.title}`,
    description: `Phone: ${venue?.contact?.phone}\nLink to reservation: ${reservationUrl}`,
    status: 'CONFIRMED',
    busyStatus: 'BUSY',
  };
}
